import { useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clipboard,
  Copy,
  Film,
  Flame,
  Gauge,
  Image as ImageIcon,
  LayoutDashboard,
  Lightbulb,
  Link2,
  Loader2,
  Mail,
  Megaphone,
  Menu,
  PlayCircle,
  Rocket,
  Save,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Wand2,
  Youtube,
} from "lucide-react";
import { toast, Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const starterBrief = {
  channel_name: "Yeni Kanal",
  niche: "YouTube içerik stratejisi, kanal büyütme ve yaratıcı üretim",
  target_audience: "Türkiye ve Avrupa'da YouTube kanalı büyütmek isteyen içerik üreticileri",
  language: "Türkçe",
  tone: "Net, profesyonel, enerjik ve uygulanabilir",
  publishing_frequency: "Haftada 3 video + 5 Shorts",
  goal: "İzlenme süresini, abone dönüşümünü ve düzenli üretim disiplinini artırmak",
  competitors: "YouTube eğitim kanalları, içerik stratejisi kanalları, sosyal medya danışmanları",
  sample_titles: [
    "Kanalını 30 Günde Daha Profesyonel Gösteren 7 Hamle",
    "YouTube Algoritması İçin İlk 10 Saniyeyi Nasıl Tasarlarsın?",
    "Shorts'tan Uzun Videoya İzleyici Taşıma Sistemi",
  ],
};

const initialStrategy = {
  summary: "Kanalın için strateji üretildiğinde burada net bir büyüme özeti görünecek.",
  ideas: [],
  calendar: [],
  recommendations: [],
};

const navItems = [
  { id: "dashboard", label: "Kontrol Merkezi", icon: LayoutDashboard },
  { id: "connections", label: "Bağlantılar", icon: Link2 },
  { id: "strategy", label: "AI Strateji", icon: Wand2 },
  { id: "titles", label: "Başlık Lab", icon: Flame },
  { id: "calendar", label: "Takvim", icon: CalendarDays },
  { id: "audit", label: "Kanal Audit", icon: Gauge },
  { id: "promote", label: "Kanal Vitrini", icon: Megaphone },
  { id: "contact", label: "İletişim", icon: Mail },
];

const servicePackages = [
  {
    title: "Kanal Kurulum Paketi",
    price: "1.490 SEK",
    description: "Kanal kimliği, açıklama, içerik sütunları, ilk 30 günlük video planı.",
    points: ["Kanal konumlandırma", "Video seri yapısı", "Profil ve açıklama metinleri"],
  },
  {
    title: "Büyüme Stratejisi",
    price: "2.900 SEK",
    description: "Mevcut kanalın audit edilir; başlık, thumbnail, format ve yayın sistemi kurulur.",
    points: ["Kanal audit raporu", "20 video fikri", "4 haftalık yayın takvimi"],
  },
  {
    title: "Creator Mentorluğu",
    price: "Aylık",
    description: "Düzenli üretim yapan kanallar için haftalık takip ve içerik koçluğu.",
    points: ["Haftalık üretim planı", "Performans değerlendirme", "Sponsor hazırlığı"],
  },
];

const titleAngles = [
  "Bunu bilmeden YouTube'a başlama",
  "Küçük kanalların yaptığı en pahalı hata",
  "30 günde daha profesyonel kanal sistemi",
  "İzleyiciyi ilk 10 saniyede tutmanın yolu",
  "Shorts izleyicisini aboneye çevirme planı",
  "Başlık ve thumbnail kontrol listesi",
  "Kanalını marka gibi gösterecek 9 detay",
  "Haftada 3 video üretmek için basit sistem",
];

function formatNumber(value) {
  return new Intl.NumberFormat("tr-TR").format(Number(value || 0));
}

function copyText(text) {
  navigator.clipboard?.writeText(text);
  toast.success("Kopyalandı");
}

function getApiError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return error?.message || "İşlem tamamlanamadı.";
}

function normalizeStrategy(payload, fallback = initialStrategy) {
  const source = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
  const fallbackSource = fallback && typeof fallback === "object" ? fallback : initialStrategy;

  return {
    summary:
      typeof source.summary === "string" && source.summary.trim()
        ? source.summary
        : fallbackSource.summary || initialStrategy.summary,
    ideas: Array.isArray(source.ideas) ? source.ideas : fallbackSource.ideas || [],
    calendar: Array.isArray(source.calendar) ? source.calendar : fallbackSource.calendar || [],
    recommendations: Array.isArray(source.recommendations)
      ? source.recommendations
      : fallbackSource.recommendations || [],
  };
}

function parseYoutubeIdentity(value) {
  const input = String(value || "").trim();
  if (!input) return { label: "Kanal bekleniyor", handle: "", url: "" };

  const handleMatch = input.match(/@([A-Za-z0-9._-]+)/);
  const channelMatch = input.match(/youtube\.com\/channel\/([^/?#]+)/i);
  const userMatch = input.match(/youtube\.com\/(?:c|user)\/([^/?#]+)/i);
  const shortMatch = input.match(/youtu\.be\/([^/?#]+)/i);
  const handle = handleMatch?.[1] || channelMatch?.[1] || userMatch?.[1] || shortMatch?.[1] || input.replace(/^@/, "");

  return {
    label: handle.startsWith("UC") ? "YouTube Kanal ID" : `@${handle.replace(/^@/, "")}`,
    handle: handle.replace(/^@/, ""),
    url: input.startsWith("http") ? input : `https://youtube.com/@${handle.replace(/^@/, "")}`,
  };
}

function buildConnectedChannelPlan(form, channelData = null) {
  const identity = parseYoutubeIdentity(form.url);
  const channel = channelData?.channel;
  const latestVideos = Array.isArray(channelData?.latestVideos) ? channelData.latestVideos : [];
  const liveIdentity = channel?.title
    ? {
        label: channel.title,
        handle: identity.handle,
        url: identity.url,
      }
    : identity;
  const niche = form.niche || "YouTube içerik üretimi";
  const audience = form.audience || "Yeni izleyiciler ve düzenli takipçiler";
  const target = form.target || "Daha fazla izlenme, abone ve düzenli üretim sistemi";
  const stage = form.stage || "Başlangıç";
  const rhythm = form.rhythm || "Haftada 2 uzun video + 4 Shorts";
  const statistics = channel?.statistics;
  const metrics = statistics
    ? [
        { label: "Abone", value: statistics.hiddenSubscribers ? "Gizli" : formatNumber(statistics.subscribers) },
        { label: "Toplam izlenme", value: formatNumber(statistics.views) },
        { label: "Video", value: formatNumber(statistics.videos) },
      ]
    : [];

  const pillars = [
    {
      title: "Arama niyetli rehberler",
      text: `${niche} konusunda insanların Google/YouTube'da aradığı net sorulara cevap veren videolar.`,
    },
    {
      title: "Seri içerikler",
      text: `İzleyiciyi geri döndüren, aynı formatta devam eden 5-8 bölümlük mini seriler.`,
    },
    {
      title: "Shorts yakalama sistemi",
      text: `Uzun videodaki en güçlü anları Shorts'a çevirip ana videoya trafik taşıyan kısa içerikler.`,
    },
    {
      title: "Güven ve otorite içerikleri",
      text: `Kanal sahibinin deneyimini, sonuçlarını ve arka planını gösteren samimi videolar.`,
    },
  ];

  const nextVideos = [
    `${niche} için yeni başlayanların yaptığı 7 hata`,
    `${stage} kanallar için 30 günlük büyüme planı`,
    `${audience} için en iyi içerik formatları`,
    `${niche} kanalında ilk 10 saniye nasıl tasarlanır?`,
    `Shorts'tan uzun videoya izleyici taşıma sistemi`,
    `${target} için haftalık üretim rutini`,
  ];
  const videoBasedIdeas = latestVideos.slice(0, 3).map((video) => `Devam videosu: ${video.title}`);
  const allNextVideos = [...videoBasedIdeas, ...nextVideos].slice(0, 6);

  const strategy = normalizeStrategy({
    summary: `${liveIdentity.label} kanalı için odak: ${niche} alanında net seri yapısı, düzenli yayın ritmi ve Shorts destekli uzun video büyümesi.`,
    ideas: allNextVideos.map((title, index) => ({
      title,
      hook: `İlk 5 saniyede ${audience} için net problemi göster ve videonun sonunda hangi sonucu alacaklarını söyle.`,
      format: index % 3 === 0 ? "Uzun video" : index % 3 === 1 ? "Shorts" : "Seri",
      target_audience: audience,
      expected_impact: "Daha net konumlandırma, daha yüksek izlenme süresi ve abone dönüşümü.",
      thumbnail_prompt: `Büyük okunaklı metin, net yüz/obje, yüksek kontrast, ${niche} teması.`,
    })),
    calendar: Array.from({ length: 14 }).map((_, index) => ({
      day: `Gün ${index + 1}`,
      content_type: index % 3 === 0 ? "Uzun video" : "Shorts",
      title: nextVideos[index % nextVideos.length],
      production_checklist: ["Kanca yaz", "Başlık varyasyonu çıkar", "Thumbnail taslağı yap", "CTA belirle"],
      publish_goal: index % 3 === 0 ? "İzlenme süresini artır" : "Yeni izleyici yakala",
    })),
    recommendations: [
      {
        title: "Kanal vaadini tek cümleye indir",
        priority: "Yüksek",
        impact: "Yeni ziyaretçi kanalı daha hızlı anlar.",
        effort: "Düşük",
        why: "Belirsiz kanal konumu abone dönüşümünü düşürür.",
        action_steps: ["Banner metnini sadeleştir", "Hakkında bölümünü güncelle", "Sabitlenen videoyu belirle"],
      },
      {
        title: "3 ana seri oluştur",
        priority: "Yüksek",
        impact: "İzleyici geri dönüşünü artırır.",
        effort: "Orta",
        why: "Tek tek videolar yerine seri mantığı kanal hafızası oluşturur.",
        action_steps: ["Eğitim serisi", "Analiz serisi", "Hızlı ipucu serisi"],
      },
      {
        title: "Shorts'u uzun videoya bağla",
        priority: "Orta",
        impact: "Kısa içerikten ana videolara trafik taşır.",
        effort: "Orta",
        why: "Shorts tek başına abone getirse bile derin izlenme oluşturmayabilir.",
        action_steps: ["Her uzun videodan 3 Shorts çıkar", "Yorumda ana videoya yönlendir", "Aynı görsel dili kullan"],
      },
    ],
  });

  return {
    identity: liveIdentity,
    status: channel ? "Canlı kanal verisi bağlandı" : form.url ? "Kanal linki alındı" : "Bağlantı beklemede",
    summary: channel
      ? `${liveIdentity.label} için gerçek kanal verisi alındı. ${rhythm} ritminde, ${target.toLowerCase()} odaklı büyüme yolu güncellendi.`
      : `${liveIdentity.label} için ${rhythm} ritminde, ${target.toLowerCase()} odaklı bir kanal büyütme yolu hazırlandı.`,
    metrics,
    latestVideos,
    pillars,
    nextVideos: allNextVideos,
    roadmap: [
      "Kanal profilini tek değer vaadine göre düzenle.",
      "İlk 14 gün için 2 uzun video ve 6 Shorts üret.",
      "Her video için 3 başlık ve 2 thumbnail varyasyonu hazırla.",
      "Yorumları içerik fikrine çevirip ikinci hafta seri başlat.",
      "30. günde CTR, izlenme süresi ve abone dönüşümünü karşılaştır.",
    ],
    metrics: ["CTR hedefi: %6-9", "İlk 30 saniye tutma: %65+", "Haftalık ritim: düzenli yayın", "Ana hedef: aboneye dönüşen izleyici"],
    strategy,
  };
}

function Sidebar({ activePage, setActivePage, close }) {
  return (
    <aside className="creator-sidebar">
      <div className="brand-lockup">
        <div className="brand-mark">
          <Youtube size={22} />
        </div>
        <div>
          <strong>KanalPilot AI</strong>
          <span>YouTube büyüme stüdyosu</span>
        </div>
      </div>

      <nav className="nav-stack" aria-label="Ana menü">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activePage === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setActivePage(item.id);
                close?.();
              }}
              className={active ? "nav-item active" : "nav-item"}
            >
              <Icon size={18} />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="sidebar-note">
        <Sparkles size={18} />
        <strong>AI üretim motoru</strong>
        <p>Fikir, takvim, başlık, thumbnail ve kanal aksiyonlarını tek panelde üret.</p>
      </div>
    </aside>
  );
}

function Topbar({ activePage, setActivePage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const current = navItems.find((item) => item.id === activePage)?.label || "Kontrol Merkezi";
  return (
    <header className="topbar">
      <div className="topbar-left">
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="mobile-menu-button">
              <Menu size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0">
            <SheetTitle className="sr-only">Mobil menü</SheetTitle>
            <Sidebar activePage={activePage} setActivePage={setActivePage} close={() => setMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        <div>
          <span>Panel</span>
          <h1>{current}</h1>
        </div>
      </div>
      <div className="topbar-search">
        <Search size={16} />
        <input readOnly placeholder="Fikir, başlık veya proje ara" />
      </div>
      <Badge className="status-badge">YouTube Yardım Platformu</Badge>
    </header>
  );
}

function Metric({ icon: Icon, label, value, detail }) {
  return (
    <Card className="metric-card">
      <CardContent className="p-5">
        <div className="metric-head">
          <span>{label}</span>
          <Icon size={20} />
        </div>
        <strong>{value}</strong>
        <p>{detail}</p>
      </CardContent>
    </Card>
  );
}

function Dashboard({ setActivePage, strategy, projects }) {
  const safeStrategy = normalizeStrategy(strategy);

  return (
    <div className="page-grid">
      <section className="hero-panel">
        <div className="hero-copy">
          <Badge className="hero-badge">
            <PlayCircle size={14} /> YouTube içerik ve kanal yardım merkezi
          </Badge>
          <h2>Kanalını fikirden yayına kadar daha profesyonel yönet.</h2>
          <p>
            KanalPilot AI; içerik fikri, başlık, thumbnail yönü, yayın takvimi,
            kanal audit'i ve üretici vitrini için tek merkezli bir çalışma alanı sunar.
          </p>
          <div className="hero-actions">
            <Button onClick={() => setActivePage("strategy")}>
              AI strateji üret <ArrowRight size={17} />
            </Button>
            <Button variant="secondary" onClick={() => setActivePage("titles")}>
              Başlık lab aç
            </Button>
          </div>
        </div>
        <div className="video-command-board" aria-label="YouTube çalışma panosu">
          <div className="preview-player">
            <div className="play-orb"><Youtube size={42} /></div>
            <div className="timeline"><span /></div>
          </div>
          <div className="mini-panels">
            <div><strong>Hook</strong><span>İlk 10 saniye</span></div>
            <div><strong>Retention</strong><span>İzlenme süresi</span></div>
            <div><strong>CTR</strong><span>Başlık + thumbnail</span></div>
          </div>
        </div>
      </section>

      <div className="metrics-grid">
        <Metric icon={Lightbulb} label="Fikir Havuzu" value={safeStrategy.ideas.length || 0} detail="AI çıktısındaki video fikirleri" />
        <Metric icon={CalendarDays} label="Takvim" value={safeStrategy.calendar.length || 0} detail="Planlanan yayın görevleri" />
        <Metric icon={Clipboard} label="Aksiyon" value={safeStrategy.recommendations.length || 0} detail="Kanal iyileştirme maddeleri" />
        <Metric icon={Save} label="Projeler" value={projects.length || 0} detail="Kaydedilen strateji setleri" />
      </div>

      <section className="split-grid">
        <Card className="workspace-card">
          <CardHeader>
            <CardTitle>Ne sunuyor?</CardTitle>
            <CardDescription>Bu site artık Pokemon mağazası değil; YouTube üreticileri için hizmet ve AI paneli.</CardDescription>
          </CardHeader>
          <CardContent className="feature-list">
            {[
              ["Kanal konumlandırma", "Niș, hedef kitle ve format sistemini netleştirir."],
              ["İçerik stratejisi", "Video fikirleri, seri planı ve yayın takvimi üretir."],
              ["Başlık & thumbnail", "CTR yükseltmeye dönük varyasyonlar hazırlar."],
              ["Creator vitrini", "Küçük kanalların başvurabileceği tanıtım alanı sunar."],
            ].map(([title, text]) => (
              <div key={title} className="feature-row">
                <CheckCircle2 size={18} />
                <div><strong>{title}</strong><p>{text}</p></div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="workspace-card">
          <CardHeader>
            <CardTitle>Hizmet paketleri</CardTitle>
            <CardDescription>Danışmanlık, audit ve içerik planı satmak için hazır yapı.</CardDescription>
          </CardHeader>
          <CardContent className="package-stack">
            {servicePackages.map((pack) => (
              <div className="package-row" key={pack.title}>
                <div>
                  <strong>{pack.title}</strong>
                  <p>{pack.description}</p>
                </div>
                <span>{pack.price}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function ConnectionsPage({ setBrief, setStrategy, setProjects }) {
  const [form, setForm] = useState({
    url: "",
    niche: "YouTube kanal büyütme, içerik stratejisi ve yaratıcı üretim",
    audience: "Kanalını büyütmek isteyen yeni ve orta seviye içerik üreticileri",
    target: "İzlenme süresi, abone dönüşümü ve düzenli içerik sistemi kurmak",
    stage: "Başlangıç",
    rhythm: "Haftada 2 uzun video + 4 Shorts",
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backendNote, setBackendNote] = useState("");
  const currentPlan = plan || buildConnectedChannelPlan(form);
  const connected = Boolean(plan);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const generateConnectionPlan = async () => {
    if (!form.url.trim()) {
      toast.error("Önce YouTube kanal linkini veya @handle bilgisini gir.");
      return;
    }

    setLoading(true);
    setBackendNote("");

    try {
      let channelData = null;

      if (BACKEND_URL) {
        const response = await axios.get(`${API}/youtube/channel`, {
          params: { ref: form.url.trim() },
        });
        channelData = response.data;
        setBackendNote("YouTube verisi backend üzerinden alındı.");
      } else {
        setBackendNote("Backend URL yok; şimdilik link ve hedef bilgilerine göre hızlı plan üretildi.");
      }

      const nextPlan = buildConnectedChannelPlan(form, channelData);
      setPlan(nextPlan);
      setStrategy(nextPlan.strategy);
      setBrief((prev) => ({
        ...prev,
        channel_name: nextPlan.identity.label,
        niche: form.niche,
        target_audience: form.audience,
        publishing_frequency: form.rhythm,
        goal: form.target,
      }));
      toast.success(channelData ? "YouTube verisi bağlandı" : "Kanal yolu hazırlandı");
    } catch (error) {
      const nextPlan = buildConnectedChannelPlan(form);
      setPlan(nextPlan);
      setStrategy(nextPlan.strategy);
      setBackendNote(`Backend bağlantısı kurulamadı; hızlı plan üretildi. ${getApiError(error)}`);
      toast.warning("Backend yok veya kanal bulunamadı; hızlı plan üretildi");
    } finally {
      setLoading(false);
    }
  };

  const saveConnectionProject = () => {
    const savedPlan = plan || buildConnectedChannelPlan(form);
    const project = {
      id: crypto.randomUUID?.() || String(Date.now()),
      name: `${savedPlan.identity.label} - Bağlantı Planı`,
      created_at: new Date().toISOString(),
      brief: form,
      strategy: savedPlan.strategy,
    };
    setProjects((prev) => [project, ...prev]);
    toast.success("Bağlantı planı kaydedildi");
  };

  return (
    <div className="connections-page">
      <div className="connection-toolbar">
        <div>
          <Badge className={connected ? "eligible-badge" : "neutral-badge"}>
            {connected ? "Kanal planı hazır" : "Bağlantı beklemede"}
          </Badge>
          <h2>Bağlantılar</h2>
          <p>YouTube kanal linkini ekle; KanalPilot sana büyüme yolu, içerik türleri ve ilk üretim planını çıkarsın.</p>
        </div>
        <div className="form-actions">
          <Button variant="secondary" onClick={saveConnectionProject}><Save size={16} /> Projeyi kaydet</Button>
          <Button onClick={generateConnectionPlan} disabled={loading}>
            {loading ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
            {loading ? "Bağlanıyor" : "Hızlı üret"}
          </Button>
        </div>
      </div>

      <div className="connection-grid">
        <Card className="workspace-card connection-card">
          <CardHeader>
            <div className="connection-title">
              <Youtube size={26} />
              <div>
                <CardTitle>YouTube bağlantısı</CardTitle>
                <CardDescription>Gerçek API bağlanana kadar link + hedef bilgisiyle profesyonel plan üretir.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="form-grid one">
            <label>
              <Label>Kanal linki veya @handle</Label>
              <Input
                value={form.url}
                onChange={(e) => update("url", e.target.value)}
                placeholder="https://youtube.com/@kanalin veya @kanalin"
              />
            </label>
            <label>
              <Label>Kanal konusu</Label>
              <Textarea value={form.niche} onChange={(e) => update("niche", e.target.value)} />
            </label>
            <label>
              <Label>Hedef kitle</Label>
              <Textarea value={form.audience} onChange={(e) => update("audience", e.target.value)} />
            </label>
            <label>
              <Label>Ana hedef</Label>
              <Textarea value={form.target} onChange={(e) => update("target", e.target.value)} />
            </label>
            <div className="form-grid">
              <label>
                <Label>Kanal seviyesi</Label>
                <Select value={form.stage} onValueChange={(value) => update("stage", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Başlangıç", "Büyüme aşaması", "Düzenli üretici", "Marka / şirket kanalı"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                  </SelectContent>
                </Select>
              </label>
              <label>
                <Label>Yayın ritmi</Label>
                <Input value={form.rhythm} onChange={(e) => update("rhythm", e.target.value)} />
              </label>
            </div>
          </CardContent>
        </Card>

        <Card className="workspace-card connection-card">
          <CardHeader>
            <div className="connection-status">
              <Badge>{currentPlan.status}</Badge>
              <span>{currentPlan.identity.label}</span>
            </div>
            <CardTitle>Kanal büyüme yolu</CardTitle>
            <CardDescription>{currentPlan.summary}</CardDescription>
            {backendNote && <p className="connection-note">{backendNote}</p>}
          </CardHeader>
          {currentPlan.metrics.length > 0 && (
            <CardContent className="connection-metrics">
              {currentPlan.metrics.map((metric) => (
                <div className="connection-metric" key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </div>
              ))}
            </CardContent>
          )}
          <CardContent className="roadmap-list">
            {currentPlan.roadmap.map((item, index) => (
              <div className="roadmap-item" key={item}>
                <span>{index + 1}</span>
                <p>{item}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="connection-grid secondary">
        <Card className="workspace-card">
          <CardHeader>
            <CardTitle>Ne tür içerikler yapmalı?</CardTitle>
            <CardDescription>Kanalı büyütmek için önerilen ana içerik sütunları.</CardDescription>
          </CardHeader>
          <CardContent className="pillar-grid">
            {currentPlan.pillars.map((pillar) => (
              <div className="pillar-card" key={pillar.title}>
                <Lightbulb size={18} />
                <strong>{pillar.title}</strong>
                <p>{pillar.text}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="workspace-card">
          <CardHeader>
            <CardTitle>İlk video fikirleri</CardTitle>
            <CardDescription>Bağlanan kanala göre hızlı başlangıç listesi.</CardDescription>
          </CardHeader>
          <CardContent className="idea-list">
            {currentPlan.nextVideos.map((title) => (
              <div className="idea-item compact" key={title}>
                <strong>{title}</strong>
                <button type="button" onClick={() => copyText(title)}><Copy size={14} /> Kopyala</button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {currentPlan.latestVideos.length > 0 && (
        <Card className="workspace-card">
          <CardHeader>
            <CardTitle>Son videolardan fırsatlar</CardTitle>
            <CardDescription>Backend bağlandığında son yüklenen videolar stratejiye sinyal olarak eklenir.</CardDescription>
          </CardHeader>
          <CardContent className="latest-video-grid">
            {currentPlan.latestVideos.slice(0, 4).map((video) => (
              <div className="latest-video-card" key={video.id || video.title}>
                {video.thumbnail && <img src={video.thumbnail} alt="" loading="lazy" />}
                <strong>{video.title}</strong>
                <span>{video.publishedAt ? new Date(video.publishedAt).toLocaleDateString("tr-TR") : "Yeni video"}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="workspace-card backend-card">
        <CardHeader>
          <CardTitle>Ücretsiz backend yolu</CardTitle>
          <CardDescription>Gerçek YouTube istatistiklerini göstermek için API anahtarını frontend'e koymadan bu akışı kur.</CardDescription>
        </CardHeader>
        <CardContent className="setup-steps">
          {[
            ["YouTube Data API v3", "Google Cloud'da ücretsiz kota ile API açılır ve kanal verileri çekilir."],
            ["Cloudflare Worker", "Ücretsiz serverless endpoint kurulur; API anahtarı gizli ortam değişkeninde tutulur."],
            ["Endpoint", "/api/youtube/channel?ref=@kanal şeklinde frontend buraya istek atar."],
            ["KanalPilot", "Gelen abone, izlenme ve video verileri büyüme planını gerçek veriye göre günceller."],
          ].map(([title, text]) => (
            <div className="setup-step" key={title}>
              <ShieldCheck size={18} />
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function StrategyPage({ brief, setBrief, strategy, setStrategy, setProjects }) {
  const [loading, setLoading] = useState(false);
  const sampleTitles = brief.sample_titles.join("\n");

  const update = (key, value) => setBrief((prev) => ({ ...prev, [key]: value }));

  const generateLocalFallback = () => {
    const base = brief.niche || "YouTube büyüme";
    return {
      summary: `${brief.channel_name} için net seri yapısı, güçlü ilk 10 saniye kancaları ve düzenli yayın takvimi öncelikli büyüme alanı.`,
      ideas: titleAngles.slice(0, 8).map((angle, index) => ({
        title: `${angle}: ${base}`,
        hook: `İlk 5 saniyede izleyiciye net sorun göster: "${base} konusunda çoğu kanal burada kaybediyor."`,
        format: index % 3 === 0 ? "Uzun video" : index % 3 === 1 ? "Shorts" : "Seri",
        target_audience: brief.target_audience,
        expected_impact: "Daha yüksek tıklama oranı ve daha net izleyici beklentisi.",
        thumbnail_prompt: `Büyük okunaklı metin, yüz ifadesi, YouTube ekranı, ${base} teması.`,
      })),
      calendar: Array.from({ length: 10 }).map((_, index) => ({
        day: `Hafta ${Math.floor(index / 5) + 1} - Gün ${index + 1}`,
        content_type: index % 2 ? "Shorts" : "Uzun video",
        title: titleAngles[index % titleAngles.length],
        production_checklist: ["Hook yaz", "3 ana bölüm çıkar", "Thumbnail taslağı yap", "CTA belirle"],
        publish_goal: "İzleyiciyi sonraki videoya taşı",
      })),
      recommendations: [
        {
          title: "İlk 10 saniye standardı oluştur",
          priority: "Yüksek",
          impact: "Retention artışı",
          effort: "Orta",
          why: "İzleyici ilk saniyelerde videonun değerini anlamalı.",
          action_steps: ["Sorunu göster", "Sonucu vaat et", "Gereksiz intro kullanma"],
        },
        {
          title: "Thumbnail dilini sabitle",
          priority: "Yüksek",
          impact: "CTR artışı",
          effort: "Orta",
          why: "Kanalın profesyonel görünmesi için görsel tutarlılık gerekir.",
          action_steps: ["2 ana renk seç", "3 kelimeyi geçme", "Yüz/obje kontrastı kullan"],
        },
        {
          title: "Seri mantığı kur",
          priority: "Orta",
          impact: "Geri dönüş oranı",
          effort: "Düşük",
          why: "Tekil videolar yerine seri içerikler kanal hafızası oluşturur.",
          action_steps: ["3 ana seri belirle", "Her seri için 10 başlık yaz", "Playlist aç"],
        },
      ],
    };
  };

  const generate = async () => {
    setLoading(true);
    try {
      const fallback = normalizeStrategy(generateLocalFallback());

      if (!BACKEND_URL) {
        setStrategy(fallback);
        toast.success("Strateji üretildi");
        return;
      }

      const response = await axios.post(`${API}/ai/strategy`, { context: brief, weeks: 2, idea_count: 10 });
      if (!response.data || typeof response.data !== "object" || !Array.isArray(response.data.ideas)) {
        throw new Error("AI servisi geçerli strateji verisi döndürmedi.");
      }

      setStrategy(normalizeStrategy(response.data, fallback));
      toast.success("AI strateji üretildi");
    } catch (error) {
      const fallback = normalizeStrategy(generateLocalFallback());
      setStrategy(fallback);
      toast.warning("Yerel strateji üretildi", { description: getApiError(error) });
    } finally {
      setLoading(false);
    }
  };

  const saveProject = () => {
    const project = {
      id: crypto.randomUUID?.() || String(Date.now()),
      name: `${brief.channel_name} - YouTube Strateji`,
      created_at: new Date().toISOString(),
      brief,
      strategy,
    };
    setProjects((prev) => [project, ...prev]);
    toast.success("Proje yerel olarak kaydedildi");
  };

  return (
    <div className="strategy-layout">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Kanal brief'i</CardTitle>
          <CardDescription>AI stratejinin doğru çıkması için kanalın temel bilgisini gir.</CardDescription>
        </CardHeader>
        <CardContent className="form-grid">
          <label><Label>Kanal adı</Label><Input value={brief.channel_name} onChange={(e) => update("channel_name", e.target.value)} /></label>
          <label><Label>Yayın ritmi</Label><Input value={brief.publishing_frequency} onChange={(e) => update("publishing_frequency", e.target.value)} /></label>
          <label className="wide"><Label>Niş / konu alanı</Label><Textarea value={brief.niche} onChange={(e) => update("niche", e.target.value)} /></label>
          <label className="wide"><Label>Hedef kitle</Label><Textarea value={brief.target_audience} onChange={(e) => update("target_audience", e.target.value)} /></label>
          <label><Label>Dil</Label><Input value={brief.language} onChange={(e) => update("language", e.target.value)} /></label>
          <label><Label>Ton</Label><Input value={brief.tone} onChange={(e) => update("tone", e.target.value)} /></label>
          <label className="wide"><Label>Ana hedef</Label><Textarea value={brief.goal} onChange={(e) => update("goal", e.target.value)} /></label>
          <label className="wide"><Label>Örnek başlıklar</Label><Textarea value={sampleTitles} onChange={(e) => update("sample_titles", e.target.value.split("\n").filter(Boolean))} /></label>
          <div className="form-actions wide">
            <Button onClick={generate} disabled={loading}>{loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Strateji üret</Button>
            <Button variant="secondary" onClick={saveProject}><Save size={16} /> Projeyi kaydet</Button>
          </div>
        </CardContent>
      </Card>

      <StrategyOutput strategy={strategy} />
    </div>
  );
}

function StrategyOutput({ strategy }) {
  const safeStrategy = normalizeStrategy(strategy);

  return (
    <div className="output-stack">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Strateji özeti</CardTitle>
          <CardDescription>Üretilecek kanal yönü.</CardDescription>
        </CardHeader>
        <CardContent><p className="summary-box">{safeStrategy.summary}</p></CardContent>
      </Card>

      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Video fikirleri</CardTitle>
          <CardDescription>Başlık, hook ve thumbnail yönleri.</CardDescription>
        </CardHeader>
        <CardContent className="idea-list">
          {safeStrategy.ideas.length === 0 ? <EmptyState text="Henüz fikir üretilmedi." /> : safeStrategy.ideas.map((idea, index) => (
            <div className="idea-item" key={`${idea.title}-${index}`}>
              <Badge>{idea.format}</Badge>
              <strong>{idea.title}</strong>
              <p>{idea.hook}</p>
              <button type="button" onClick={() => copyText(`${idea.title}\n${idea.hook}`)}><Copy size={14} /> Kopyala</button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function TitlesPage() {
  const [topic, setTopic] = useState("YouTube kanal büyütme");
  const [audience, setAudience] = useState("Yeni başlayan içerik üreticileri");
  const variants = useMemo(() => {
    const clean = topic || "YouTube";
    return [
      `${clean} için kimsenin anlatmadığı gerçek sistem`,
      `${clean}: Yeni başlayanların yaptığı 7 kritik hata`,
      `30 günde ${clean} konusunda daha profesyonel görün`,
      `${audience} için ${clean} rehberi`,
      `Bunu yapmadan ${clean} videosu çekme`,
      `${clean} ile izleyiciyi ilk 10 saniyede yakala`,
    ];
  }, [topic, audience]);

  return (
    <div className="two-column">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Başlık & thumbnail lab</CardTitle>
          <CardDescription>Video konusundan tıklanabilir başlık ve görsel yön üret.</CardDescription>
        </CardHeader>
        <CardContent className="form-grid one">
          <label><Label>Video konusu</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} /></label>
          <label><Label>Hedef izleyici</Label><Input value={audience} onChange={(e) => setAudience(e.target.value)} /></label>
          <div className="thumbnail-frame">
            <div>
              <span>THUMBNAIL</span>
              <strong>{topic}</strong>
              <p>Net yüz ifadesi + 3 kelimelik güçlü metin + yüksek kontrast</p>
            </div>
            <ImageIcon size={52} />
          </div>
        </CardContent>
      </Card>

      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Başlık varyasyonları</CardTitle>
          <CardDescription>CTR için farklı açıları hızlı test et.</CardDescription>
        </CardHeader>
        <CardContent className="idea-list">
          {variants.map((title) => (
            <div className="idea-item compact" key={title}>
              <strong>{title}</strong>
              <button type="button" onClick={() => copyText(title)}><Copy size={14} /> Kopyala</button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function CalendarPage({ strategy }) {
  const safeStrategy = normalizeStrategy(strategy);
  const items = safeStrategy.calendar.length ? safeStrategy.calendar : Array.from({ length: 7 }).map((_, index) => ({
    day: `Gün ${index + 1}`,
    content_type: index % 2 ? "Shorts" : "Uzun video",
    title: titleAngles[index],
    production_checklist: ["Araştırma", "Script", "Çekim", "Edit", "Yayın"],
    publish_goal: "Düzenli üretim temposu",
  }));

  return (
    <Card className="workspace-card">
      <CardHeader>
        <CardTitle>Yayın takvimi</CardTitle>
        <CardDescription>Haftalık üretim ritmini tek bakışta yönet.</CardDescription>
      </CardHeader>
      <CardContent className="calendar-grid">
        {items.map((item, index) => (
          <div className="calendar-card" key={`${item.day}-${index}`}>
            <Badge variant="outline">{item.content_type}</Badge>
            <span>{item.day}</span>
            <strong>{item.title}</strong>
            <ul>{(item.production_checklist || []).slice(0, 4).map((step) => <li key={step}>{step}</li>)}</ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AuditPage() {
  const [subs, setSubs] = useState("1200");
  const [views, setViews] = useState("85000");
  const score = Math.min(100, Math.round((Number(subs || 0) / 100) + (Number(views || 0) / 2500)));
  return (
    <div className="two-column">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Kanal audit</CardTitle>
          <CardDescription>Hızlı sağlık skoru ve öncelikli aksiyonlar.</CardDescription>
        </CardHeader>
        <CardContent className="form-grid one">
          <label><Label>Abone</Label><Input type="number" value={subs} onChange={(e) => setSubs(e.target.value)} /></label>
          <label><Label>Toplam izlenme</Label><Input type="number" value={views} onChange={(e) => setViews(e.target.value)} /></label>
          <div className="score-ring">
            <strong>{score}</strong>
            <span>Kanal hazırlık skoru</span>
          </div>
        </CardContent>
      </Card>
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Öncelikler</CardTitle>
          <CardDescription>Bu skor için önerilen düzeltmeler.</CardDescription>
        </CardHeader>
        <CardContent className="feature-list">
          {["Kapak görselinde değer vaadini netleştir", "İlk 10 saniye script şablonu kullan", "Her video için 3 thumbnail varyasyonu hazırla", "Shorts videolarını uzun videoya bağla"].map((item) => (
            <div className="feature-row" key={item}><Target size={18} /><div><strong>{item}</strong><p>Uygulanabilir ve ölçülebilir aksiyon.</p></div></div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PromotePage() {
  const [form, setForm] = useState({ name: "", link: "", email: "", subscribers: "", views: "", category: "Eğitim" });
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const eligible = Number(form.subscribers || 0) < 5000 && Number(form.views || 0) >= 50000;
  return (
    <div className="two-column">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Kanal Vitrini</CardTitle>
          <CardDescription>Küçük ama emek veren kanalları ücretsiz öne çıkarma alanı.</CardDescription>
        </CardHeader>
        <CardContent className="feature-list">
          <div className="feature-row"><Users size={18} /><div><strong>5.000 abone altı</strong><p>Yeni kanallara alan açmak için üst limit.</p></div></div>
          <div className="feature-row"><BarChart3 size={18} /><div><strong>50.000+ toplam izlenme</strong><p>Üretim emeği ve potansiyeli göstermek için alt sınır.</p></div></div>
          <div className="feature-row"><ShieldCheck size={18} /><div><strong>Manuel inceleme</strong><p>Başvurular kalite ve kategori uyumu açısından kontrol edilir.</p></div></div>
        </CardContent>
      </Card>
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Başvuru formu</CardTitle>
          <CardDescription>Vitrine aday kanal bilgilerini gir.</CardDescription>
        </CardHeader>
        <CardContent className="form-grid one">
          <label><Label>Kanal adı</Label><Input value={form.name} onChange={(e) => update("name", e.target.value)} /></label>
          <label><Label>Kanal linki</Label><Input value={form.link} onChange={(e) => update("link", e.target.value)} placeholder="https://youtube.com/@kanal" /></label>
          <label><Label>E-posta</Label><Input value={form.email} onChange={(e) => update("email", e.target.value)} /></label>
          <label><Label>Abone</Label><Input type="number" value={form.subscribers} onChange={(e) => update("subscribers", e.target.value)} /></label>
          <label><Label>Toplam izlenme</Label><Input type="number" value={form.views} onChange={(e) => update("views", e.target.value)} /></label>
          <label>
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(value) => update("category", value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Eğitim", "Oyun", "Teknoloji", "Vlog", "Müzik", "Spor", "Komedi"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <Badge className={eligible ? "eligible-badge" : "neutral-badge"}>{eligible ? "Uygun görünüyor" : "Kriterleri tamamla"}</Badge>
          <Button onClick={() => toast.success("Başvuru örnek olarak alındı")}>Başvuru gönder</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ContactPage() {
  return (
    <div className="two-column">
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>İletişim</CardTitle>
          <CardDescription>YouTube kanal desteği, audit, danışmanlık ve iş birliği için.</CardDescription>
        </CardHeader>
        <CardContent className="contact-panel">
          <a href="mailto:pokesgod@gmail.com"><Mail size={18} /> pokesgod@gmail.com</a>
          <a href="https://youtube.com" target="_blank" rel="noreferrer"><Youtube size={18} /> YouTube kanal linki eklenebilir</a>
          <p>Form şimdilik hızlı iletişim taslağı olarak çalışır. Gerçek mail gönderimi için backend mail servisi bağlanabilir.</p>
        </CardContent>
      </Card>
      <Card className="workspace-card">
        <CardHeader>
          <CardTitle>Mesaj taslağı</CardTitle>
          <CardDescription>Müşteri talebini net toplamak için.</CardDescription>
        </CardHeader>
        <CardContent className="form-grid one">
          <label><Label>Ad</Label><Input /></label>
          <label><Label>E-posta</Label><Input /></label>
          <label><Label>Mesaj</Label><Textarea placeholder="Kanalım için audit veya strateji istiyorum..." /></label>
          <Button onClick={() => toast.success("Mesaj taslağı hazır")}>Gönder <Send size={16} /></Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ProjectsBar({ projects }) {
  if (!projects.length) return null;
  return (
    <Card className="workspace-card">
      <CardHeader>
        <CardTitle>Kaydedilen projeler</CardTitle>
        <CardDescription>Bu oturumda kaydedilen stratejiler.</CardDescription>
      </CardHeader>
      <CardContent className="package-stack">
        {projects.map((project) => (
          <div className="package-row" key={project.id}>
            <div><strong>{project.name}</strong><p>{new Date(project.created_at).toLocaleString("tr-TR")}</p></div>
            <span>{project.strategy?.ideas?.length || 0} fikir</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function EmptyState({ text }) {
  return <div className="empty-state"><Rocket size={22} /> {text}</div>;
}

function MainApp() {
  const [activePage, setActivePage] = useState("dashboard");
  const [brief, setBrief] = useState(starterBrief);
  const [strategy, setStrategy] = useState(initialStrategy);
  const [projects, setProjects] = useState([]);

  const renderPage = () => {
    if (activePage === "dashboard") return <Dashboard setActivePage={setActivePage} strategy={strategy} projects={projects} />;
    if (activePage === "connections") return <ConnectionsPage setBrief={setBrief} setStrategy={setStrategy} setProjects={setProjects} />;
    if (activePage === "strategy") return <StrategyPage brief={brief} setBrief={setBrief} strategy={strategy} setStrategy={setStrategy} setProjects={setProjects} />;
    if (activePage === "titles") return <TitlesPage />;
    if (activePage === "calendar") return <CalendarPage strategy={strategy} />;
    if (activePage === "audit") return <AuditPage />;
    if (activePage === "promote") return <PromotePage />;
    if (activePage === "contact") return <ContactPage />;
    return null;
  };

  return (
    <>
      <Toaster richColors position="top-right" />
      <div className="app-shell creator-app">
        <div className="desktop-sidebar">
          <Sidebar activePage={activePage} setActivePage={setActivePage} />
        </div>
        <div className="app-main">
          <Topbar activePage={activePage} setActivePage={setActivePage} />
          <main className="main-content">
            {renderPage()}
            <ProjectsBar projects={projects} />
          </main>
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
